// src/services/studyPlanService.js
// Content Velocity Model - Task-Based Study Plan Generation
// Supports both StudyPlanWizard payload and OnboardingModal payload (testDate, placementAnswers, focusAreas).
// No Reality Check / daily minute calculations; timeline from testDate or default 8 weeks.

import { getFailedGrammarPillars } from './placementToGrammarMapper';
import { PLACEMENT_QUESTIONS, getCorrectIndex } from '../components/onboarding/placementQuestions';

/**
 * Content Inventory - Dynamically extracts exact content requirements from JSON files
 * This replaces percentage-based time allocation with exact content counts
 */

/**
 * Load and parse vocabulary inventory from JSON files
 */
const loadVocabularyInventory = async (targetLevel, langCode = 'en') => {
  try {
    const response = await fetch(`/data/vocabulary/vocabulary-${targetLevel.toLowerCase()}_${langCode}.json`);
    if (!response.ok) {
      // Fallback to English if language not available
      if (langCode !== 'en') {
        return await loadVocabularyInventory(targetLevel, 'en');
      }
      return [];
    }
    
    const data = await response.json();
    if (!data.categories) return [];
    
    const topics = [];
    for (const [categoryKey, category] of Object.entries(data.categories)) {
      let wordCount = 0;
      // Count words across all subcategories
      for (const subcat of Object.values(category.subcategories || {})) {
        wordCount += (subcat.cards || []).length;
      }
      
      if (wordCount > 0) {
        topics.push({
          id: categoryKey,
          name: category.name,
          words: wordCount,
        });
      }
    }
    
    return topics.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error(`Failed to load vocabulary inventory for ${targetLevel}:`, error);
    return [];
  }
};

/**
 * Get grammar pillar display name from grammarTopicsMap
 */
const getGrammarPillarName = (pillarId, level) => {
  // Import grammar topics mapping (using the structure from grammarTopicsMap.js)
  const GRAMMAR_TOPICS_BY_LEVEL = {
    'B1': {
      'connectors': 'Sentence Structure & Connectors',
      'passive': 'The Passive Voice',
      'subjunctive': 'Konjunktiv II',
      'relative-clauses': 'Relative Clauses',
      'prepositions': 'Verbs with Fixed Prepositions',
      'adjectives': 'Adjective Declension',
      'genitive': 'The Genitive Case',
      'verb-cases': 'Verbs with Case Complements',
      'infinitive-zu': "Infinitive with 'zu'",
      'noun-verb-connections': 'Noun-Verb Connections',
      'participles-adjectives': 'Participles as Adjectives',
      'reflexive': 'Reflexive Verbs',
    },
    'B2': {
      'connectors_b2': 'Complex Sentences: The Art of Connection',
      'moods_and_voice_b2': 'The Verb in Command: Mood & Voice',
      'nominal_structures_b2': 'Advanced Nominal Structures',
      'stylistic_refinement_b2': 'Stylistic Refinement',
      'pronouns_b2': 'Pronouns: The Art of Substitution',
      'prepositions_b2': 'Prepositions: Mastering Case and Meaning',
      'adjectives_b2': 'Adjectives in Detail: Description and Comparison',
      'tenses_b2': 'Mastery of Time: Verb Tenses (Tempora)',
      'cases_b2': 'Cases (B2)',
    },
    'C1': {
      'connectors_c1': 'Advanced Sentence Connectors',
      'nominal_vs_verbal_styles': 'Nominal vs. Verbal Styles',
      'passive_alternatives': 'The Passive Voice & Its Alternatives',
      'subjunctive_moods_c1': 'The Subjunctive Mood',
      'participial_constructions_c1': 'Participle Constructions',
      'subjective_use_of_modal_verbs': 'Subjective Use of Modal Verbs',
      'noun_verb_combinations': 'Noun-Verb Combinations',
      'complex_adjective_declension_c1': 'Adjectives with Fixed Prepositions',
    },
  };
  
  const levelTopics = GRAMMAR_TOPICS_BY_LEVEL[level];
  if (levelTopics && levelTopics[pillarId]) {
    return levelTopics[pillarId];
  }
  
  // Fallback: capitalize and format the pillar ID
  return pillarId.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Load and parse grammar inventory from JSON files
 */
const loadGrammarInventory = async (targetLevel) => {
  try {
    const response = await fetch(`/data/grammar/grammar-${targetLevel.toLowerCase()}.json`);
    if (!response.ok) return [];
    
    const data = await response.json();
    if (!data.exercises) return [];
    
    // Group exercises by pillar and count
    const pillarMap = new Map();
    for (const exercise of data.exercises) {
      const pillar = exercise.pillar;
      if (!pillar) continue;
      
      if (!pillarMap.has(pillar)) {
        const pillarName = getGrammarPillarName(pillar, targetLevel);
        pillarMap.set(pillar, {
          id: pillar,
          name: pillarName,
          exercises: 0,
        });
      }
      pillarMap.get(pillar).exercises++;
    }
    
    return Array.from(pillarMap.values());
  } catch (error) {
    console.error(`Failed to load grammar inventory for ${targetLevel}:`, error);
    return [];
  }
};

/**
 * Load and parse Redemittel inventory from JSON files
 */
const loadRedemittelInventory = async (targetExam, targetLevel) => {
  try {
    const examKey = `${targetExam.toLowerCase()}-${targetLevel.toLowerCase()}`;
    const response = await fetch(`/data/redemittel/${examKey}-redemittel.json`);
    if (!response.ok) return null;
    
    const data = await response.json();
    if (!data.skills) return null;
    
    let writing = 0;
    let speaking = 0;
    
    // Count cards in writing (schreiben) and speaking (sprechen) sections
    if (data.skills.schreiben) {
      for (const part of data.skills.schreiben.parts || []) {
        writing += (part.cards || []).length;
      }
    }
    
    if (data.skills.sprechen) {
      for (const part of data.skills.sprechen.parts || []) {
        speaking += (part.cards || []).length;
      }
    }
    
    return {
      writing,
      speaking,
      total: writing + speaking,
    };
  } catch (error) {
    console.error(`Failed to load Redemittel inventory for ${targetExam} ${targetLevel}:`, error);
    return null;
  }
};

/**
 * Compute total weeks from today until testDate (ISO date string).
 * If testDate is missing or invalid, returns defaultWeeks (default 8).
 * Minimum 1 week.
 */
export function getTotalWeeksFromTestDate(testDate, defaultWeeks = 8) {
  if (!testDate || typeof testDate !== 'string') return Math.max(1, defaultWeeks);
  const target = new Date(testDate);
  if (Number.isNaN(target.getTime())) return Math.max(1, defaultWeeks);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  if (target <= today) return Math.max(1, defaultWeeks);
  const diffMs = target - today;
  const weeks = Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000));
  return Math.max(1, weeks);
}

/**
 * Get phase based on week number (updated for 5-phase system)
 */
const getPhase = (week, totalWeeks) => {
  if (week === 1) return 'intro';
  const progress = week / totalWeeks;
  if (progress <= 0.5) return 'foundation'; // Weeks 2-50%
  if (progress <= 0.8) return 'intensive'; // Weeks 51-80%
  return 'final'; // Weeks 81-100%
};

const getWeekTitle = (week, totalWeeks) => {
  const phase = getPhase(week, totalWeeks);
  if (phase === 'intro') return 'Einführung & Diagnostik';
  if (phase === 'foundation') return 'Grundlagen aufbauen';
  if (phase === 'intensive') return 'Intensives Training';
  return 'Finale Vorbereitung';
};

const getWeekFocus = (week, totalWeeks) => {
  const phase = getPhase(week, totalWeeks);
  if (phase === 'intro') return ['Startanalyse', 'Lernrhythmus aufbauen'];
  if (phase === 'foundation') return ['Wortschatzbasis', 'Grammatik verstehen'];
  if (phase === 'intensive') return ['Prüfungslektionen', 'Redemittel intensiv'];
  return ['Prüfungssimulationen', 'Finale Redemittel-Wiederholung'];
};

/**
 * Fetch study plan from JSON file
 */
const fetchStudyPlan = async (provider, level) => {
  const examKey = `${provider.toLowerCase()}-${level.toLowerCase()}`;
  try {
    const response = await fetch(`/data/lessons/study-plans/${examKey}-study-plan.json`);
    if (!response.ok) {
      console.warn(`Study plan not found for ${examKey}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch study plan for ${examKey}:`, error);
    return null;
  }
};

/**
 * Fetch Redemittel data
 */
const fetchRedemittel = async (provider, level) => {
  const examKey = `${provider.toLowerCase()}-${level.toLowerCase()}`;
  try {
    const response = await fetch(`/data/redemittel/${examKey}-redemittel.json`);
    if (!response.ok) {
      console.warn(`Redemittel not found for ${examKey}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch Redemittel for ${examKey}:`, error);
    return null;
  }
};

/**
 * Distribute vocabulary topics across weeks using flat, concurrent distribution
 * All topics are evenly distributed starting from Week 1
 */
const distributeVocabularyTopics = async (targetLevel, week, totalWeeks) => {
  const topics = await loadVocabularyInventory(targetLevel);
  if (topics.length === 0) return [];
  
  // Flat distribution: calculate items per week
  const topicsPerWeek = Math.ceil(topics.length / totalWeeks);
  
  // Calculate which topics belong to this week (0-indexed)
  const weekIndex = week - 1; // Week 1 is index 0
  const startIdx = weekIndex * topicsPerWeek;
  const endIdx = Math.min(startIdx + topicsPerWeek, topics.length);
  
  // Return topics for this week, or empty array if we've exhausted all topics
  if (startIdx >= topics.length) {
    return []; // All topics distributed, return empty for review mode
  }
  
  return topics.slice(startIdx, endIdx);
};

/**
 * Distribute grammar pillars across weeks using flat, concurrent distribution.
 * All pillars are evenly distributed starting from Week 1.
 * If failedPillarIds is provided, pillars are reordered so failed (diagnostic) pillars
 * come first and are therefore front-loaded into earlier weeks.
 */
const distributeGrammarPillars = async (targetLevel, week, totalWeeks, grammarFocus, failedPillarIds = []) => {
  let pillars = await loadGrammarInventory(targetLevel);
  if (pillars.length === 0) return { lessons: [], exercises: 0 };

  if (failedPillarIds.length > 0) {
    const failedSet = new Set(failedPillarIds);
    pillars = [
      ...pillars.filter((p) => failedSet.has(p.id)),
      ...pillars.filter((p) => !failedSet.has(p.id)),
    ];
  }

  // Flat distribution: calculate pillars per week
  const pillarsPerWeek = Math.ceil(pillars.length / totalWeeks);

  // Calculate which pillars belong to this week (0-indexed)
  const weekIndex = week - 1; // Week 1 is index 0
  const startIdx = weekIndex * pillarsPerWeek;
  const endIdx = Math.min(startIdx + pillarsPerWeek, pillars.length);

  // Return pillars for this week, or empty if exhausted
  if (startIdx >= pillars.length) {
    // All pillars distributed - return review exercises only
    const exercisesPerPillar = grammarFocus === 'practice' ? 20 :
                               grammarFocus === 'learn' ? 10 : 15;
    return {
      lessons: [],
      exercises: Math.ceil((pillars.length * exercisesPerPillar) / totalWeeks), // Review exercises
    };
  }

  const assignedPillars = pillars.slice(startIdx, endIdx);

  // Calculate exercises based on grammarFocus
  const exercisesPerPillar = grammarFocus === 'practice' ? 20 :
                             grammarFocus === 'learn' ? 10 : 15;
  const totalExercises = assignedPillars.length * exercisesPerPillar;

  return {
    lessons: assignedPillars,
    exercises: totalExercises,
  };
};

/**
 * Distribute Redemittel across weeks using flat, concurrent distribution
 * All Redemittel are evenly distributed starting from Week 1
 */
const distributeRedemittel = async (targetExam, targetLevel, week, totalWeeks) => {
  const redemittel = await loadRedemittelInventory(targetExam, targetLevel);
  if (!redemittel) return { writing: 0, speaking: 0 };
  
  // Flat distribution: calculate items per week for writing and speaking separately
  const writingPerWeek = Math.ceil(redemittel.writing / totalWeeks);
  const speakingPerWeek = Math.ceil(redemittel.speaking / totalWeeks);
  
  // Calculate which items belong to this week (0-indexed)
  const weekIndex = week - 1; // Week 1 is index 0
  
  // Calculate start and end indices for this week
  const writingStartIdx = weekIndex * writingPerWeek;
  const writingEndIdx = Math.min(writingStartIdx + writingPerWeek, redemittel.writing);
  const writingCount = Math.max(0, writingEndIdx - writingStartIdx);
  
  const speakingStartIdx = weekIndex * speakingPerWeek;
  const speakingEndIdx = Math.min(speakingStartIdx + speakingPerWeek, redemittel.speaking);
  const speakingCount = Math.max(0, speakingEndIdx - speakingStartIdx);
  
  return {
    writing: writingCount,
    speaking: speakingCount,
  };
};

/**
 * Distribute exam lessons across weeks using flat, concurrent distribution
 * All lessons are evenly distributed starting from Week 1
 */
const distributeExamLessons = (studyPlan, week, totalWeeks) => {
  if (!studyPlan || !studyPlan.modules) return [];
  
  const allLessons = studyPlan.modules
    .flatMap(m => m.lessons || [])
    .filter(l => l.type !== 'video' && 
            ['theory_practice', 'strategy', 'practice', 'speaking_practice', 'writing_practice'].includes(l.type));
  
  if (allLessons.length === 0) return [];
  
  // Flat distribution: calculate lessons per week
  const lessonsPerWeek = Math.ceil(allLessons.length / totalWeeks);
  
  // Calculate which lessons belong to this week (0-indexed)
  const weekIndex = week - 1; // Week 1 is index 0
  const startIdx = weekIndex * lessonsPerWeek;
  const endIdx = Math.min(startIdx + lessonsPerWeek, allLessons.length);
  
  // Return lessons for this week, or empty array if exhausted
  if (startIdx >= allLessons.length) {
    return []; // All lessons distributed
  }
  
  return allLessons.slice(startIdx, endIdx);
};

/**
 * Schedule exam simulations (exactly 6, starting from Week 2)
 * Distributes simulations evenly between Week 2 and totalWeeks
 * Handles edge cases: short timelines may have multiple simulations per week
 * NEW: Implements Cognitive Stamina Scaling - adjusts mock exam scheduling to prevent cognitive fatigue
 */
const scheduleExamSimulations = (week, totalWeeks) => {
  // Simulations start from Week 2
  if (week < 2) return null;
  
  // Calculate available weeks for simulations (Week 2 to totalWeeks)
  const availableWeeks = totalWeeks - 1; // Exclude Week 1
  const totalSimulations = 6;
  
  // Cognitive Stamina Scaling Logic
  const isShortTimeline = totalWeeks <= 4;
  // For timelines > 4 weeks: Final 4 weeks strictly are full mocks (180min)
  // Weeks 2 through (totalWeeks - 4) are mini-mocks (45min) to train accuracy
  const finalMonthStartWeek = totalWeeks > 4 ? totalWeeks - 3 : 2; // First week of final 4 weeks
  
  // Determine if this week is in the final month
  const isFinalMonth = week >= finalMonthStartWeek;
  
  // Calculate which simulations should occur in which weeks
  const simulationWeeks = []; // Array of { week: number, isFull: boolean, duration: string }
  
  if (isShortTimeline) {
    // Short timeline (<= 4 weeks): All simulations are full mocks starting from Week 2
    // Distribute 6 simulations across available weeks
    const simulationsPerWeek = Math.ceil(totalSimulations / availableWeeks);
    let simulationCount = 0;
    
    for (let w = 2; w <= totalWeeks && simulationCount < totalSimulations; w++) {
      const remainingSimulations = totalSimulations - simulationCount;
      const simulationsThisWeek = Math.min(simulationsPerWeek, remainingSimulations);
      
      // Add this week multiple times if needed (all full mocks)
      for (let i = 0; i < simulationsThisWeek; i++) {
        simulationWeeks.push({ week: w, isFull: true, duration: '180min' });
        simulationCount++;
      }
    }
  } else {
    // Longer timeline (> 4 weeks): Progressive stamina training
    // Early weeks (Week 2 through totalWeeks - 4): Mini-Mocks (45min)
    // Final 4 weeks: Full Mocks (180min)
    
    // Distribute simulations evenly
    if (availableWeeks >= totalSimulations) {
      const spacing = availableWeeks / totalSimulations;
      for (let i = 0; i < totalSimulations; i++) {
        const targetWeek = Math.round(2 + i * spacing);
        const weekNum = Math.max(2, Math.min(targetWeek, totalWeeks));
        const isInFinalMonth = weekNum >= finalMonthStartWeek;
        
        simulationWeeks.push({
          week: weekNum,
          isFull: isInFinalMonth,
          duration: isInFinalMonth ? '180min' : '45min'
        });
      }
    } else {
      // Edge case: distribute across available weeks
      const simulationsPerWeek = Math.ceil(totalSimulations / availableWeeks);
      let simulationCount = 0;
      
      for (let w = 2; w <= totalWeeks && simulationCount < totalSimulations; w++) {
        const remainingSimulations = totalSimulations - simulationCount;
        const simulationsThisWeek = Math.min(simulationsPerWeek, remainingSimulations);
        const isInFinalMonth = w >= finalMonthStartWeek;
        
        for (let i = 0; i < simulationsThisWeek; i++) {
          simulationWeeks.push({
            week: w,
            isFull: isInFinalMonth,
            duration: isInFinalMonth ? '180min' : '45min'
          });
          simulationCount++;
        }
      }
    }
  }
  
  // Count how many simulations occur in this specific week
  const simulationsThisWeek = simulationWeeks.filter(s => s.week === week);
  
  if (simulationsThisWeek.length > 0) {
    // Return the first simulation's config (all simulations in same week have same config)
    const firstSim = simulationsThisWeek[0];
    return {
      type: 'exam_simulation',
      isFull: firstSim.isFull,
      duration: firstSim.duration,
      count: simulationsThisWeek.length,
    };
  }
  
  return null;
};

/**
 * Build weekly tasks using Content Velocity model with concurrent distribution
 * All content buckets are distributed starting from Week 1 (except simulations from Week 2)
 * Implements Compensatory Scoring Routing based on targetExam and weakSkills.
 * failedPillarIds: grammar pillars to front-load (from placement diagnostic wrong answers).
 */
const buildWeeklyTasks = async (week, targetExam, targetLevel, studyPlan, redemittelData, totalWeeks, grammarFocus, weakSkills = [], failedPillarIds = [], purpose = 'both') => {
  const tasks = [];

  // ── Purpose-based weighting ──────────────────────────────────────────────
  // exam    → exam practice (lesen/hören/schreiben + mock) is primary; vocab/grammar secondary
  // training → vocab/grammar is primary; exam practice secondary
  // both    → balanced; all sections included
  const isExamFocus = purpose === 'exam';
  const isTrainingFocus = purpose === 'training';

  // Vocab / grammar inclusion threshold:
  //   exam mode → include only on odd weeks (≈50% frequency)
  //   training  → always include (100%)
  //   both      → always include (100%)
  const skipVocabGrammarThisWeek = isExamFocus && week % 2 === 0;

  // Exam lesson volume multiplier (only used as a skip guard for training mode):
  //   training mode → include only every other week
  const skipExamLessonsThisWeek = isTrainingFocus && week % 2 === 0;

  // Exam simulation count:
  //   training mode → cap at 3 simulations total (skip alternate weeks)
  const skipSimulationThisWeek = isTrainingFocus && week % 2 === 0;

  // Lesen / Hören / Schreiben practice task inclusion:
  //   exam     → all three every week
  //   both     → rotate: lesen odd weeks, hören even weeks, schreiben every 3rd week
  //   training → appear every 3rd week (low frequency)
  const includeLesenThisWeek    = isExamFocus || ((!isTrainingFocus) && week % 2 === 1) || (isTrainingFocus && week % 3 === 1);
  const includeHorenThisWeek    = isExamFocus || ((!isTrainingFocus) && week % 2 === 0) || (isTrainingFocus && week % 3 === 2);
  const includeSchreibenThisWeek = isExamFocus || ((!isTrainingFocus) && week % 3 === 0) || (isTrainingFocus && week % 4 === 0);

  // Compensatory Scoring Routing: Adjust distribution based on exam type and weak skills
  const isTELC = targetExam && targetExam.toUpperCase() === 'TELC';
  const isGoethe = targetExam && (targetExam.toUpperCase() === 'GOETHE' || targetExam.toUpperCase() === 'ÖSD');
  const hasWeakWriting = weakSkills.includes('writing');
  const hasWeakSpeaking = weakSkills.includes('speaking');
  const hasWeakReading = weakSkills.includes('reading');
  
  // Calculate adjustment multipliers
  let readingMultiplier = 1.0;
  let redemittelWritingMultiplier = 1.0;
  let redemittelSpeakingMultiplier = 1.0;
  let weakSkillMultiplier = 1.0;
  
  if (isTELC) {
    // TELC: Compensatory logic - build safety net with Reading if Writing/Speaking are weak
    if (hasWeakWriting || hasWeakSpeaking) {
      readingMultiplier = 1.2; // Increase Reading by 20% to build safety net
      // Heavily increase Redemittel for Writing if it's a weak skill
      if (hasWeakWriting) {
        redemittelWritingMultiplier = 1.5; // Increase Writing Redemittel by 50%
      }
      if (hasWeakSpeaking) {
        redemittelSpeakingMultiplier = 1.3; // Increase Speaking Redemittel by 30%
      }
    }
  } else if (isGoethe) {
    // Goethe/ÖSD: Modular logic - must pass every section, aggressively increase weak skills
    if (hasWeakWriting) {
      redemittelWritingMultiplier = 1.6; // Aggressively increase Writing Redemittel
      weakSkillMultiplier = 1.4; // Increase overall task volume for weak skills
    }
    if (hasWeakSpeaking) {
      redemittelSpeakingMultiplier = 1.5; // Aggressively increase Speaking Redemittel
      weakSkillMultiplier = 1.4;
    }
    if (hasWeakReading) {
      readingMultiplier = 1.5; // Aggressively increase Reading tasks
    }
  }
  
  // ── 1. Lesen / Hören / Schreiben Practice Tasks ──────────────────────────
  // In exam focus mode these appear every week (high priority, listed first).
  // In both mode they rotate each week. In training mode they appear sparingly.
  if (includeLesenThisWeek) {
    tasks.push({
      id: `${week}-lesen-practice`,
      type: 'lesen_practice',
      title: 'Lesen-Training',
      description: 'Prüfungsaufgaben: Leseverstehen üben',
      lessonIds: [],
    });
  }
  if (includeHorenThisWeek) {
    tasks.push({
      id: `${week}-horen-practice`,
      type: 'horen_practice',
      title: 'Hören-Training',
      description: 'Prüfungsaufgaben: Hörverstehen üben',
      lessonIds: [],
    });
  }
  if (includeSchreibenThisWeek) {
    tasks.push({
      id: `${week}-schreiben-practice`,
      type: 'schreiben_practice',
      title: 'Schreiben-Training',
      description: 'Prüfungsaufgaben: Schriftliche Produktion üben',
      lessonIds: [],
    });
  }

  // ── 2. Vocabulary Task (exact topics) - Starting Week 1 ──────────────────
  // In exam focus mode, skip on even weeks (secondary priority).
  if (!skipVocabGrammarThisWeek) {
    let vocabTopics = await distributeVocabularyTopics(targetLevel, week, totalWeeks);
    if (vocabTopics.length > 0 && readingMultiplier > 1.0) {
      const baseCount = vocabTopics.length;
      const targetCount = Math.ceil(baseCount * readingMultiplier);
      const allTopics = await loadVocabularyInventory(targetLevel);
      if (allTopics.length > 0) {
        const topicsPerWeek = Math.ceil(allTopics.length / totalWeeks);
        const weekIndex = week - 1;
        const startIdx = weekIndex * topicsPerWeek;
        const endIdx = Math.min(startIdx + targetCount, allTopics.length);
        vocabTopics = allTopics.slice(startIdx, endIdx);
      }
    }
    if (vocabTopics.length > 0) {
      const topicNames = vocabTopics.map(t => `${t.name} (${t.words} Wörter)`).join(', ');
      tasks.push({
        id: `${week}-vocab`,
        type: 'vocabulary',
        title: 'Wortschatz-Training (SRS)',
        description: `Meistere ${vocabTopics.length} Themen: ${topicNames}`,
        topics: vocabTopics.map(t => ({ id: t.id, name: t.name, words: t.words })),
        lessonIds: [],
      });
    }
  }

  // ── 3. Grammar Task (exact pillars + exercise quota) - Starting Week 1 ───
  // In exam focus mode, skip on even weeks (secondary priority).
  if (!skipVocabGrammarThisWeek) {
    const grammarIsWeak = weakSkills.includes('grammar');
    const effectiveGrammarFocus = grammarIsWeak && weakSkillMultiplier > 1.0
      ? (grammarFocus === 'practice' ? 'practice' : 'balanced')
      : grammarFocus;
    const grammarDistribution = await distributeGrammarPillars(targetLevel, week, totalWeeks, effectiveGrammarFocus, failedPillarIds);
    if (grammarDistribution.lessons.length > 0 || grammarDistribution.exercises > 0) {
      if (grammarDistribution.lessons.length > 0) {
        const pillarNames = grammarDistribution.lessons.map(p => p.name).join(', ');
        tasks.push({
          id: `${week}-grammar-lessons`,
          type: 'grammar_lessons',
          title: 'Grammatik lernen (Lektionen)',
          description: `${grammarDistribution.lessons.length} Grammatik-Pfeiler: ${pillarNames}`,
          pillars: grammarDistribution.lessons.map(p => ({ id: p.id, name: p.name })),
          lessonIds: [],
        });
      }
      if (grammarDistribution.exercises > 0) {
        const adjustedExercises = grammarIsWeak && weakSkillMultiplier > 1.0
          ? Math.ceil(grammarDistribution.exercises * weakSkillMultiplier)
          : grammarDistribution.exercises;
        tasks.push({
          id: `${week}-grammar-exercises`,
          type: 'grammar_exercises',
          title: 'Grammatik üben (Übungen)',
          description: `Vervollständige ${adjustedExercises} Übungen`,
          exerciseCount: adjustedExercises,
          lessonIds: [],
        });
      }
    }
  }

  // ── 4. Redemittel Task (exact phrases to memorize) - Starting Week 1 ─────
  const redemittelDistribution = await distributeRedemittel(targetExam, targetLevel, week, totalWeeks);
  const adjustedWriting = Math.ceil(redemittelDistribution.writing * redemittelWritingMultiplier);
  const adjustedSpeaking = Math.ceil(redemittelDistribution.speaking * redemittelSpeakingMultiplier);
  if (adjustedWriting > 0 || adjustedSpeaking > 0) {
    tasks.push({
      id: `${week}-redemittel`,
      type: 'redemittel',
      title: 'Redemittel (Lego-Blöcke)',
      description: `Memoriere ${adjustedWriting} Schreiben-Redemittel, ${adjustedSpeaking} Sprechen-Redemittel`,
      writing: adjustedWriting,
      speaking: adjustedSpeaking,
      lessonIds: [],
    });
  }

  // ── 5. Exam Lessons Task (exact modules) - Starting Week 1 ───────────────
  // In training mode, skip on even weeks (secondary priority).
  if (!skipExamLessonsThisWeek) {
    const examLessons = distributeExamLessons(studyPlan, week, totalWeeks);
    if (examLessons.length > 0) {
      let filteredLessons = examLessons;
      if (weakSkillMultiplier > 1.0 && weakSkills.length > 0) {
        const skillRelevantLessons = examLessons.filter(lesson => {
          const titleLower = (lesson.title || '').toLowerCase();
          if (hasWeakWriting && (titleLower.includes('schreiben') || titleLower.includes('writing'))) return true;
          if (hasWeakSpeaking && (titleLower.includes('sprechen') || titleLower.includes('speaking'))) return true;
          if (hasWeakReading && (titleLower.includes('lesen') || titleLower.includes('reading'))) return true;
          return false;
        });
        if (skillRelevantLessons.length > 0) {
          const additionalCount = Math.ceil(examLessons.length * (weakSkillMultiplier - 1.0));
          filteredLessons = [...skillRelevantLessons, ...examLessons.slice(0, additionalCount)];
        }
      }
      if (filteredLessons.length > 0) {
        const lessonTitles = filteredLessons.map(l => l.title).join(', ');
        tasks.push({
          id: `${week}-exam-lessons`,
          type: 'exam_lessons',
          title: `Prüfungslektionen (${targetExam} ${targetLevel})`,
          description: `${filteredLessons.length} Lektionen: ${lessonTitles}`,
          lessonIds: filteredLessons.map(l => l.lessonId),
          lessons: filteredLessons,
        });
      }
    }
  }

  // ── 6. Exam Simulations Task (up to 6, starting Week 2) ──────────────────
  // In training mode, skip on even weeks (cap to ~3 simulations).
  if (!skipSimulationThisWeek) {
    const simulation = scheduleExamSimulations(week, totalWeeks);
    if (simulation) {
      const simulationCount = simulation.count || 1;
      for (let i = 0; i < simulationCount; i++) {
        tasks.push({
          id: `${week}-exam-simulation-${i + 1}`,
          type: 'exam_simulation',
          title: simulationCount > 1 ? `Prüfungssimulation ${i + 1}/${simulationCount}` : 'Prüfungssimulation',
          description: simulation.isFull ? 'Vollständige Prüfungssimulation' : 'Teilprüfungssimulation',
          isFull: simulation.isFull,
          duration: simulation.duration || '180min',
          lessonIds: [],
        });
      }
    }
  }

  return tasks;
};

/**
 * Normalize payload from OnboardingModal (examProvider, level, testDate, focusAreas, placementAnswers)
 * or StudyPlanWizard (targetExam, targetLevel, months, weakSkills) into a single shape for the algorithm.
 */
const normalizeFormData = (formData) => {
  const targetExam = formData.targetExam ?? (() => {
    const p = (formData.examProvider || '').toLowerCase();
    if (p === 'goethe') return 'Goethe';
    if (p === 'telc') return 'TELC';
    if (p === 'osd' || p === 'ösd') return 'ÖSD';
    return 'TELC';
  })();
  const targetLevel = (formData.targetLevel || formData.level || 'B2').toString().toUpperCase();
  const weakSkills = Array.isArray(formData.weakSkills) && formData.weakSkills.length > 0
    ? formData.weakSkills
    : (Array.isArray(formData.focusAreas) ? formData.focusAreas : []);
  return {
    ...formData,
    targetExam: targetExam || 'TELC',
    targetLevel,
    weakSkills,
  };
};

/**
 * Generate personalized study plan using Content Velocity model.
 * Accepts either StudyPlanWizard payload (months, targetExam, targetLevel, weakSkills, ...)
 * or OnboardingModal payload (testDate, placementAnswers, focusAreas, examProvider, level).
 * Timeline: from testDate (calendar) or months * 4, or default 8 weeks. No Reality Check.
 */
const generatePersonalizedPlan = async (formData) => {
  const normalized = normalizeFormData(formData);
  const {
    targetLevel,
    targetExam,
    weakSkills,
    grammarFocus,
    testDate,
    months,
    placementAnswers,
    purpose,
  } = normalized;

  // Dynamic timeline: testDate → weeks until exam; else months * 4; else 8 weeks
  const totalWeeks = testDate != null && testDate !== ''
    ? getTotalWeeksFromTestDate(testDate, 8)
    : (typeof months === 'number' && months >= 1 ? Math.max(1, months * 4) : 8);

  // Precision targeting: failed placement questions → grammar pillars to front-load
  let failedPillarIds = [];
  if (Array.isArray(placementAnswers) && placementAnswers.length > 0 && targetLevel) {
    failedPillarIds = getFailedGrammarPillars(PLACEMENT_QUESTIONS, placementAnswers, targetLevel, getCorrectIndex);
  }

  // Fetch study plan and Redemittel data
  const studyPlan = await fetchStudyPlan(targetExam, targetLevel);
  const redemittelData = await fetchRedemittel(targetExam, targetLevel);

  if (!studyPlan) {
    console.warn(`Could not load study plan for ${targetExam} ${targetLevel}`);
  }

  // Content inventory for return (no Reality Check)
  const [vocabTopics, grammarPillars, redemittelInv] = await Promise.all([
    loadVocabularyInventory(targetLevel),
    loadGrammarInventory(targetLevel),
    loadRedemittelInventory(targetExam, targetLevel),
  ]);
  const totalVocabWords = vocabTopics.reduce((sum, t) => sum + t.words, 0);
  const totalGrammarExercises = grammarPillars.reduce((sum, p) => sum + p.exercises, 0);
  const contentInventory = {
    vocabWords: totalVocabWords,
    grammarExercises: totalGrammarExercises,
    redemittel: redemittelInv?.total ?? 0,
    examLessons: 22,
  };

  // Generate weeks with Content Velocity model
  const generatedWeeks = [];
  for (let i = 1; i <= totalWeeks; i++) {
    const phase = getPhase(i, totalWeeks);

    const tasks = await buildWeeklyTasks(
      i,
      targetExam,
      targetLevel,
      studyPlan,
      redemittelData,
      totalWeeks,
      grammarFocus || 'balanced',
      weakSkills || [],
      failedPillarIds,
      purpose || 'both',
    );

    generatedWeeks.push({
      week: i,
      title: getWeekTitle(i, totalWeeks),
      focus: getWeekFocus(i, totalWeeks),
      phase: phase,
      tasks: tasks,
      completed: false,
    });
  }

  // Recommendation (no schedule type / daily capacity)
  const isTELC = targetExam && targetExam.toUpperCase() === 'TELC';
  const isGoethe = targetExam && (targetExam.toUpperCase() === 'GOETHE' || targetExam.toUpperCase() === 'ÖSD');
  const hasWeakWriting = weakSkills && weakSkills.includes('writing');
  const hasWeakSpeaking = weakSkills && weakSkills.includes('speaking');
  const hasWeakReading = weakSkills && weakSkills.includes('reading');

  let recommendation = '';
  if (isTELC && (hasWeakWriting || hasWeakSpeaking)) {
    recommendation = `Ihr persönlicher ${targetLevel}-Plan für ${targetExam}. Wir setzen Redemittel 'Lego-Blöcken' für ${hasWeakWriting ? 'Schreiben' : ''}${hasWeakWriting && hasWeakSpeaking ? ' und ' : ''}${hasWeakSpeaking ? 'Sprechen' : ''} ein und bauen ein starkes Lesen-Sicherheitsnetz auf.`;
  } else if (isGoethe && (hasWeakWriting || hasWeakSpeaking)) {
    recommendation = `Ihr persönlicher ${targetLevel}-Plan für ${targetExam}. Wir verstärken ${hasWeakWriting ? 'Schreiben' : ''}${hasWeakWriting && hasWeakSpeaking ? ' und ' : ''}${hasWeakSpeaking ? 'Sprechen' : ''}, damit Sie die 60%-Schwelle sicher überschreiten.`;
  } else if (isGoethe && hasWeakReading) {
    recommendation = `Ihr persönlicher ${targetLevel}-Plan für ${targetExam}. Wir verstärken das Lesen für die 60%-Schwelle.`;
  } else {
    recommendation = `Ihr persönlicher ${targetLevel}-Plan für ${targetExam}.`;
    if (weakSkills && weakSkills.length > 0) {
      recommendation += ` Fokus: ${weakSkills.join(', ')}.`;
    }
  }

  return {
    ...normalized,
    weeks: generatedWeeks,
    studyPlanData: studyPlan,
    redemittelData: redemittelData,
    contentInventory,
    totalWeeks,
    timelineAlert: null,
    recommendation,
  };
};

const studyPlanService = {
  generatePersonalizedPlan,
  getTotalWeeksFromTestDate,
};

export default studyPlanService;
